import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@folio/components/forms/select';
import { useQuery } from '@tanstack/react-query';
import { customerControllerGetCustomerQueryOptions } from 'korint-sdk';
import type { Field } from 'korint-sdk/react';
import { useEffect } from 'react';
import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';

const SMALL_MRP_SUPPORTED_EXCESSES = ['300', '500', '750', '1000'];

// ponytail: mirrors the authoritative Small-eligibility predicate in the backend
// underwriting.service (tutassurHiscoxMultirisquesPro). Kept in sync by hand; the backend
// guard is the source of truth — this only hides excesses the Small grid can't price (ENG-3880).
const SMALL_OCCUPANT_STATUSES = ['RENTER', 'RENT_FREE_OCCUPANT', 'SUBLET'];
const SMALL_ACTIVITY_CATEGORIES = ['ACTIVITES_BUREAU', 'SERVICES_PERSONNE', 'HYGIENE_SANTE_BEAUTE'];

type PolicyExternalData = {
  customers?: string[];
  branchId?: string;
  asset?: { customFields?: Record<string, unknown> };
};

function isSmallEligible(
  asset: Record<string, unknown> | undefined,
  companyActivityCategory: unknown,
): boolean {
  const surface = Number(asset?.assetSurface);
  const contentValue = Number(asset?.assetContentValue);
  return (
    Number.isFinite(surface) &&
    surface <= 75 &&
    Number.isFinite(contentValue) &&
    contentValue <= 15_000 &&
    SMALL_OCCUPANT_STATUSES.includes(String(asset?.assetOccupantStatus)) &&
    SMALL_ACTIVITY_CATEGORIES.includes(String(companyActivityCategory))
  );
}

export function HiscoxMrpExcessFormField<TFormValues extends FieldValues>({
  field,
  rhfField,
  externalData,
  portalTarget,
}: {
  field: Extract<Field, { type: 'hiscoxMrpExcess' }>;
  rhfField: ControllerRenderProps<TFormValues, Path<TFormValues>>;
  externalData?: Record<string, unknown>;
  portalTarget?: HTMLElement | null;
}) {
  const policy = externalData as PolicyExternalData | undefined;
  const customerId = policy?.customers?.[0];

  const { data: companyActivityCategory } = useQuery({
    ...customerControllerGetCustomerQueryOptions(customerId!, { branchId: policy?.branchId }),
    enabled: !!customerId,
    select: data => data.customFields?.find(cf => cf.key === 'companyActivityCategory')?.value,
  });

  const small = isSmallEligible(policy?.asset?.customFields, companyActivityCategory);
  const options = small
    ? field.options.filter(o => SMALL_MRP_SUPPORTED_EXCESSES.includes(o.value))
    : field.options;

  const value = (rhfField.value as string) || '';
  const valueAllowed = options.some(o => o.value === value);
  // highest supported excess (lowest premium) — always a member of the filtered list
  const fallbackValue = options.at(-1)?.value;

  useEffect(() => {
    if (small && value && !valueAllowed && fallbackValue) {
      rhfField.onChange(fallbackValue);
    }
  }, [small, value, valueAllowed, fallbackValue, rhfField]);

  return (
    <Select onValueChange={v => rhfField.onChange(v)} value={value}>
      <SelectTrigger name={field.name} disabled={field.disabled}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent portalTarget={portalTarget}>
        {options.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
